// sort-imports-ignore

import axios from 'axios'
import axiosRetry from 'axios-retry'

import type { AxiosConfig } from '#vue-shared-lib/types/axios'

import useOuraAuth from '#web-oura-tools/auth'

// Full config:  https://github.com/axios/axios#request-config

export const useAxios = (config: AxiosConfig) => {
  const options = {
    baseURL: config.url,
    timeout: config.timeout ?? 60 * 1000,
    headers: !sessionStorage.OuraOverrides
      ? {
          'Accept': 'application/json',
          'X-User-Agent': `OURA-${config.agent ?? 'tools'}`,
          'X-App-Version': config.version ?? 'development',
        }
      : {
          'Accept': 'application/json',
          'X-User-Agent': `OURA-${config.agent ?? 'tools'}`,
          'X-App-Version': config.version ?? 'development',
          'X-Drop-Permissions': sessionStorage.OuraOverrides,
        },
    withCredentials: config.withCredentials ?? true,
  }

  const axiosClient = axios.create(options)

  const controllers: Record<string, AbortController> = {}

  axiosRetry(axiosClient, {
    retries: config.retryCount ?? 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
  })

  function abortRequests(requestIds: string[]) {
    for (const requestId of requestIds) {
      const controller = controllers[requestId]

      if (controller) {
        controller.abort()

        delete controllers[requestId]
      }
    }
  }

  axiosClient.interceptors.request.use(
    async (request) => {
      const ouraAuth = useOuraAuth()

      await ouraAuth.authToken(request.cloudEnv ?? import.meta.env.VITE_APP_ENV)

      // Add the firebase authorization token into the headers

      if (request.headers && !request.headers['Authorization']) {
        const idToken = await ouraAuth.authToken(request.cloudEnv ?? import.meta.env.VITE_APP_ENV)

        request.headers['Authorization'] = idToken
      }

      // If permissions drop is wanted then add them to the headers

      if (request.dropPermissions) {
        request.headers['X-Drop-Permissions'] = request.dropPermissions
          .concat((sessionStorage.OuraOverrides || '').split(' '))
          .join(' ')
          .trim()
      }

      // Abort any old get request with same id/url to avoid backend overload

      const requestId = request?.id ?? axios.getUri(request)

      if (requestId && !request.signal && request.method === 'get') {
        if (controllers[requestId]) {
          controllers[requestId].abort()
        }

        controllers[requestId] = new AbortController()

        request.signal = controllers[requestId].signal
      }

      return request
    },
    (error) => {
      // Do something with request error
      return Promise.reject(error)
    },
  )

  // Add a response interceptor
  axiosClient.interceptors.response.use(
    (response) => {
      const requestId = response?.config?.id ?? axiosClient.getUri(response?.config)

      if (requestId) {
        delete controllers[requestId]
      }

      return response
    },
    (error) => {
      const requestId = error.response?.config?.id ?? axiosClient.getUri(error.response?.config)

      if (requestId) {
        delete controllers[requestId]
      }

      if (axios.isCancel(error)) {
        console.debug('Request canceled', error)

        throw error
      } else {
        if (error?.response?.status === 401) {
          const notify = new CustomEvent('notify', {
            detail: { authError: true },
          })

          document.dispatchEvent(notify)
        } else if (error?.response?.status === 500) {
          const notify = new CustomEvent('notify', {
            detail: { backendError: true },
          })

          document.dispatchEvent(notify)
        } else if (error?.response?.status === 503) {
          const notify = new CustomEvent('notify', {
            detail: { backendUpdate: true },
          })

          document.dispatchEvent(notify)
        } else if (error?.response?.status === undefined) {
          console.debug('Request failed', error)

          const notify = new CustomEvent('notify', {
            detail: { connectionError: true },
          })

          document.dispatchEvent(notify)
        }
      }

      throw error
    },
  )

  return {
    axiosClient,
    abortRequests,
  }
}
